import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output, signal, ViewChild, WritableSignal } from '@angular/core';
import { KuiDropdownComponent } from 'app/key-ui/dropdown/dropdown.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateRangeChangeEvent, TimePeriod } from '../daterange.model';
import { addDays, endOfDay, endOfToday, formatISO, parseISO, startOfDay } from 'date-fns';
import { MatchMediaService } from 'app/services';
import { toSignal } from '@angular/core/rxjs-interop';
import { KuiDropdownModule } from 'app/key-ui/dropdown';
import { DateFormatPipe } from '../date-format.pipe';
import { FormsModule } from '@angular/forms';
import { KuiActionModule } from 'app/key-ui/action';
import { KuiButtonModule } from 'app/key-ui/button';
import { KuiIconModule } from 'app/key-ui/icon';
import { KuiListModule } from 'app/key-ui/list';
import { MatchMediaServiceModule } from 'app/services/match-media/match-media.module';
import { DatePickerModule } from 'primeng/datepicker';
import { getTimePeriodForDates } from 'app/shared/utils/dates';
@Component({
    selector: 'kui-daterange-new',
    imports: [
        CommonModule,
        KuiIconModule,
        FormsModule,
        DatePickerModule,
        KuiActionModule,
        KuiListModule,
        TranslateModule,
        MatchMediaServiceModule,
        KuiDropdownModule,
        KuiButtonModule
    ],
    providers: [DateFormatPipe],    
    templateUrl: './daterange-new.component.html',
    styles: ``
})
export class KuiDateRangeNewComponent {
    
    // services
    private matchMedia = inject(MatchMediaService);
    private dateFormatPipe = inject(DateFormatPipe);
    private i18n = inject(TranslateService);

    // inputs
    startDate = input<string>();
    endDate = input<string>();
    min = input<string>();
    max = input<string>();
    dateFormat = input<string>("yyyy-MM-DD HH:mm:ss");
    inputStyle = input<'form' | 'none'>('form');
    disabled = input<boolean>(false);
    hourFormat = input<12 | 24>(24);
    icon = input<string>('calendar');
    timePeriods = input<TimePeriod[]>();
    timePeriod = input<TimePeriod>();
    customDatePicker = input<boolean>(false);
    displayTimePeriodText = input<boolean>(true);
    customContent = input<boolean>(false);
    emitInitialChange = input<boolean>(true);

    /**
     * Text to display when no date has been selected
     */
    noDatePlaceholder = input<string>();

    /**
     * Text to use for the submit button
     */
    submitButtonText = input<string>();

    /**
     * The number of days difference that can be selected
     */
    maxDateDiffDays = input<number>();

    /**
     * Specify how to handle time when selecting a date via the calendar
     */
    timeStrategy = input<'user' | 'day-extents'>('user');


    // outputs
    onChangeTimePeriod = output<DateRangeChangeEvent>();

    @ViewChild('dropdown') dropdown: KuiDropdownComponent;

    // writables
    startCalendarDate: WritableSignal<Date | null> = signal(null);
    endCalendarDate: WritableSignal<Date | null> = signal(null);
    maxDiffDate: Date;
    showDateRangePicker: WritableSignal<boolean> = signal(false);
    customDateRange: WritableSignal<Date[] | undefined> = signal(null);

    selectedEndDate: WritableSignal<Date | null> = signal(null);
    selectedStartDate: WritableSignal<Date | null> = signal(null);

    showCalendar: WritableSignal<boolean> = signal(false);

    selectedTimePeriod = signal<TimePeriod | null>(null);
    internalTimePeriod = signal<TimePeriod | null>(null);
    isMobile = toSignal(this.matchMedia.isMobile);

    // computed
    useTime = computed(() => this.timeStrategy() === 'user');
    minDate = computed(() => {
        if (this.min()) {
            return parseISO(this.min())
        }
    });
    maxDate = computed(() => {
        if (this.max()) {
            return parseISO(this.max())
        }
    });
    isFormStyle = computed(() => this.inputStyle() === 'form');
    timePeriodOptions = computed(() => {
        return this.timePeriods().map(({ text, ...rest }) => ({...rest, value: text}));
    });

    // this is done, so if the the date display does not fit in the space in the UI it can overflow with ellipsis
    timePeriodDisplayText = computed(() => {
        // if 'Today', 'Yesterday' or 'This Month' is selected we don't want to include a date range in the datepicker
        const timePeriodText = this.selectedTimePeriod()?.text.toLowerCase();
        if (timePeriodText && (timePeriodText === 'today' || timePeriodText === 'yesterday' || timePeriodText === 'this month' || timePeriodText === 'previous 7 days' || timePeriodText === 'previous 30 days')) {
           return this.selectedTimePeriod().text;
        }

        const formattedStartDate = this.dateFormatPipe.transform(this.selectedStartDate(), this.dateFormat());
        const formattedEndDate = this.dateFormatPipe.transform(this.selectedEndDate(), this.dateFormat());
        const text = [formattedStartDate, '-', formattedEndDate];

        if (this.customDateRange()?.length === 2) {
            text.unshift(`${this.i18n.instant('REPORTING.ITEMS.FIELDS.VALUES.CUSTOM')}:`);
        }

        this.selectedTimePeriod() ? text.unshift(`${this.selectedTimePeriod().text}:`) : null;
        return text.join(' ');
    })

    constructor() {}

    ngOnInit(): void {
        this.startCalendarDate.set(this.startDate() ? parseISO(this.startDate()) : undefined);
        this.endCalendarDate.set(this.endDate() ? parseISO(this.endDate()) : undefined);

        // we have initial values
        if (this.startCalendarDate() && this.endCalendarDate()) {
            if (this.emitInitialChange()) {
                this.confirmDates();
            } else {
                this.selectedStartDate.set(this.startCalendarDate());
                this.selectedEndDate.set(this.endCalendarDate());
                const timePeriodId = getTimePeriodForDates(this.startCalendarDate(), this.endCalendarDate());
                const matchedPeriod = this.timePeriods()?.find(x => x.id === timePeriodId);
                if (matchedPeriod) {
                    this.selectedTimePeriod.set(matchedPeriod);
                    this.internalTimePeriod.set(matchedPeriod);
                } else {
                    this.customDateRange.set([this.startCalendarDate(), this.endCalendarDate()]);
                }
            }
        }
        if (this.timePeriod() && this.emitInitialChange()) {
            this.selectedTimePeriod.set(this.internalTimePeriod());
            this.handleTimePeriod(this.timePeriod());
        } else if (this.timePeriod()) {
            const { start, end } = this.timePeriod().getDateRange(this.endDate() ?? formatISO(endOfToday()));
            this.startCalendarDate.set(parseISO(start));
            this.endCalendarDate.set(parseISO(end));
            this.internalTimePeriod.set(this.timePeriod());
            this.selectedTimePeriod.set(this.timePeriod());
            this.selectedStartDate.set(this.startCalendarDate());
            this.selectedEndDate.set(this.endCalendarDate());
        }
    }

    toggleDateRangePicker() {
        this.showDateRangePicker.update(value => !value);
    }

    handleCalendarDateSelection() {
        // check user has selected a start and end date
        if (this.customDateRange() && this.customDateRange().length === 2) {
            this.startCalendarDate.set(this.customDateRange()[0]);
            this.endCalendarDate.set(this.customDateRange()[1]);
        }

        this.adjustForDateDiff();
        this.adjustForTimeStrategy();
    }

    handleTimePeriod(period: TimePeriod) {
        this.customDateRange.set(null);
        const { start, end } = period.getDateRange(this.endDate() ?? formatISO(endOfToday()));
        this.startCalendarDate.set(parseISO(start));
        this.endCalendarDate.set(parseISO(end));
        this.internalTimePeriod.set(period);

        if (!this.useTime()) {
            // time is not user specified, let's just apply this period instead.
            this.confirmDates();
        }
    }

    confirmDates() {
        this.selectedStartDate.set(this.startCalendarDate());
        this.selectedEndDate.set(this.endCalendarDate());
        this.customDateRange() ? this.selectedTimePeriod.set(null) : this.selectedTimePeriod.set(this.internalTimePeriod());

        this.onChangeTimePeriod.emit({
            start: formatISO(this.selectedStartDate()),
            end: formatISO(this.selectedEndDate()),
            timePeriod: this.selectedTimePeriod()
        });

        this.showDateRangePicker.set(false);
        this.dropdown?.toggle(false);
    }

    /**
     * Adjust dates depending on if a maximum date difference value is set
     */
    private adjustForDateDiff() {
        if (this.maxDateDiffDays()) {
            const newEndDate = addDays(this.startCalendarDate(), this.maxDateDiffDays());
            // Make sure the new end date doesn't occur after the max date explicitly set by the user
            if (newEndDate > this.maxDate()) {
                this.maxDiffDate = this.maxDate();
            } else {
                this.maxDiffDate = newEndDate;
            }
            if (this.endCalendarDate() > this.maxDiffDate) {
                this.endCalendarDate.set(this.maxDiffDate);
            }
        }
    }

    /**
     * Adjust the time of selected dates depending on the specified time strategy
     */
    private adjustForTimeStrategy() {
        if (this.timeStrategy() === 'day-extents') {
            if (this.startCalendarDate()) {
                this.startCalendarDate.set(startOfDay(this.startCalendarDate()));
            }
            if (this.endCalendarDate()) {
                this.endCalendarDate.set(endOfDay(this.endCalendarDate()));
            }
        }
    }
}
